import { useCallback, useEffect, useRef, useState } from 'react';
import {
  artifactService,
  composeArtifactTitle,
  updateAIMessage,
  useAppDispatch,
  type Message,
  type MessageArtifact,
} from '@ideoz/core';
import { renderArtifactHtml, showToast } from '@ideoz/ui';
import { resolveArtifact } from '../../artifacts/renderers/registry';

/**
 * Owns the SSE-streamed artifact side panel: which artifact is open, panel
 * visibility, keeping the open snapshot in sync as the DB row lands, and the
 * save / share actions.
 *
 * @param messages              rendered messages (drives the async-sync effect)
 * @param conversationMessages  redux conversation messages (save target lookup)
 */
export function useStreamedArtifactPanel(params: {
  messages: Message[];
  conversationMessages: Message[];
}) {
  const { messages, conversationMessages } = params;
  const dispatch = useAppDispatch();

  const [activeArtifact, setActiveArtifact] = useState<MessageArtifact | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Id of the message the panel was opened from. This is the stable anchor for
  // re-syncing: after the DB row lands, the async hydration rewrites the
  // artifact's `name` (streamed name → persisted `artifact_type`) and `content`
  // (streamed text → row content), so matching by name+content breaks. Matching
  // by the owning message survives that rewrite, so the panel picks up the `id`
  // (and the Edit / Share buttons appear).
  const sourceMessageIdRef = useRef<string | null>(null);
  // Keep a live handle on the latest messages so `open` can locate the source
  // message without re-creating the callback on every render.
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;

  const open = useCallback((artifact: MessageArtifact) => {
    // Locate the message this artifact belongs to so future updates re-sync.
    const source = messagesRef.current.find((m) => {
      const art = m.artifact;
      if (!art) return false;
      if (artifact.id && art.id) return art.id === artifact.id;
      return art.name === artifact.name && art.content === artifact.content;
    });
    sourceMessageIdRef.current = source?.id ?? null;
    setActiveArtifact(artifact);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    sourceMessageIdRef.current = null;
    setIsOpen(false);
  }, []);

  /**
   * Keep the open panel in sync with the underlying message when its artifact
   * updates asynchronously. Common case: the panel opened right after a stream
   * ended, but the DB row (and thus the artifact `id`) only lands seconds later
   * once the Celery `persist_from_stream` task finishes. Without this the panel
   * keeps rendering the stale snapshot with no id, so Edit / Share stay hidden.
   */
  useEffect(() => {
    if (!activeArtifact || !isOpen) return;
    // Prefer the source-message anchor captured at open time — it survives the
    // hydration rewrite (name/content change) that would otherwise break a
    // name+content match and leave the panel stuck with no id. Fall back to
    // id / name+content matching for panels opened without a known source
    // (e.g. legacy call sites).
    const sourceId = sourceMessageIdRef.current;
    const match = messages.find((m) => {
      const art = m.artifact;
      if (!art) return false;
      if (sourceId) return m.id === sourceId;
      if (activeArtifact.id && art.id) return art.id === activeArtifact.id;
      return art.name === activeArtifact.name && art.content === activeArtifact.content;
    });
    const nextArtifact = match?.artifact;
    if (!nextArtifact) return;
    // Only push a state update when something the panel cares about actually
    // changed. Avoids a render loop.
    if (
      nextArtifact.id !== activeArtifact.id ||
      nextArtifact.demoUrl !== activeArtifact.demoUrl ||
      nextArtifact.content !== activeArtifact.content
    ) {
      setActiveArtifact(nextArtifact);
    }
  }, [messages, activeArtifact, isOpen]);

  /**
   * Return the open artifact's DB id, resolving it on demand when it hasn't
   * landed yet. The row is written by an async task, so a just-generated
   * artifact can briefly have no `id` — rather than disable Edit/Share, we look
   * the row up by the owning message's `llmInteractionId` and adopt it. Returns
   * null only when there's genuinely nothing to act against.
   */
  const ensureArtifactId = useCallback(async (): Promise<string | null> => {
    const art = activeArtifact;
    if (!art) return null;
    if (art.id) return art.id;

    const msgs = messagesRef.current;
    const sourceId = sourceMessageIdRef.current;
    const source =
      (sourceId ? msgs.find((m) => m.id === sourceId) : undefined) ??
      msgs.find(
        (m) => m.artifact && m.artifact.name === art.name && m.artifact.content === art.content
      );
    const interactionId = (source as { llmInteractionId?: string } | undefined)?.llmInteractionId;
    if (!interactionId) return null;

    try {
      const rows = await artifactService.listArtifactsByLlmInteraction(interactionId);
      const row = rows[0];
      if (!row?.id) return null;
      setActiveArtifact((prev) =>
        prev
          ? {
              ...prev,
              id: row.id,
              content: prev.content || row.content || '',
              structuredContent: prev.structuredContent ?? row.structured_content ?? null,
              demoUrl: prev.demoUrl ?? row.demo_url ?? null,
            }
          : prev
      );
      return row.id;
    } catch {
      return null;
    }
  }, [activeArtifact]);

  const notReadyToast = () =>
    showToast({
      message: 'Artifact is still finalizing — please try again in a moment.',
      severity: 'info',
    });

  // Save edits: persist to the API, reflect back into the panel and the
  // message that owns the artifact.
  const save = useCallback(
    async (content: string) => {
      const art = activeArtifact;
      if (!art) return;
      const id = art.id ?? (await ensureArtifactId());
      if (!id) return notReadyToast();
      try {
        const updated = await artifactService.updateArtifact(id, { content });
        setActiveArtifact((prev) => (prev ? { ...prev, content: updated.content ?? content } : prev));
        // Push the new content back into redux so the message's artifact stays
        // in sync with the DB. Direct mutation throws under Immer — dispatch.
        const targetMessage = conversationMessages.find((m) => m.artifact?.id === id);
        if (targetMessage) {
          dispatch(
            updateAIMessage({
              messageId: targetMessage.id,
              content: targetMessage.content,
              artifact: {
                ...targetMessage.artifact!,
                content: updated.content ?? content,
              },
            })
          );
        }
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [activeArtifact, conversationMessages, dispatch, ensureArtifactId]
  );

  // Save manual edits to a structured (JSON) artifact — PUT updates the row in
  // place. Reflects the edited JSON back into the panel and the owning message.
  const saveStructured = useCallback(
    async (json: Record<string, unknown>) => {
      const art = activeArtifact;
      if (!art) return;
      const id = art.id ?? (await ensureArtifactId());
      if (!id) return notReadyToast();
      try {
        const updated = await artifactService.updateArtifact(id, { structured_content: json });
        const nextStructured = updated.structured_content ?? json;
        setActiveArtifact((prev) =>
          prev
            ? { ...prev, structuredContent: nextStructured, content: updated.content ?? prev.content }
            : prev
        );
        // Keep the owning message's artifact in sync so the pill reopens the
        // edited version.
        const targetMessage = conversationMessages.find((m) => m.artifact?.id === id);
        if (targetMessage?.artifact) {
          dispatch(
            updateAIMessage({
              messageId: targetMessage.id,
              content: targetMessage.content,
              artifact: {
                ...targetMessage.artifact,
                structuredContent: nextStructured,
                content: updated.content ?? targetMessage.artifact.content,
              },
            })
          );
        }
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [activeArtifact, conversationMessages, dispatch, ensureArtifactId]
  );

  // Publish the streamed artifact to S3 and copy the public link.
  const share = useCallback(async () => {
    const art = activeArtifact;
    if (!art) return;
    const id = art.id ?? (await ensureArtifactId());
    if (!id) return notReadyToast();
    try {
      // Predefined types (persona, JTBD, 5 Whys, HMW, Atlas…) publish their
      // designed HTML card via the renderer; generic artifacts (no renderer)
      // publish their markdown rendered to HTML.
      const view = art.structuredContent
        ? resolveArtifact(art.structuredContent, art.name)
        : null;
      const html = view
        ? view.renderer.toHtml(view.model, composeArtifactTitle(art.structuredContent) ?? art.name)
        : renderArtifactHtml(art.content ?? '', art.name);
      const updated = await artifactService.publishArtifactToS3(id, html);
      const demoUrl = updated.demo_url ?? art.demoUrl ?? null;
      if (!demoUrl) throw new Error('Publish returned no URL');
      setActiveArtifact((prev) => (prev ? { ...prev, demoUrl } : prev));
      await navigator.clipboard.writeText(demoUrl);
      showToast({ message: 'Share link copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to share artifact', err);
      showToast({ message: 'Failed to create share link', severity: 'error' });
      throw err;
    }
  }, [activeArtifact, ensureArtifactId]);

  return { activeArtifact, isOpen, open, close, save, saveStructured, share };
}
